import React, { Fragment } from 'react';

import { useCartWithServerOrderSurcharges } from 'features/cart/hooks/use-cart-with-server-order-surcharges';

import { IServerOrder } from '@rbi-ctg/menu';
import { BottomSheet } from 'components/bottom-sheet';
import Currency from 'components/currency';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { t } from 'state/intl/translations';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';

import { Container, Total, TotalLabelTitle, TotalsRow } from './modal-order-details.styled';
import { OrderItem } from './order-item';
import { OrderPricesDetails } from './order-prices-details';
import { OrderRewardDetails } from './order-reward-details';

type ModalOrderDetailsProps = {
  showModal: boolean;
  onCloseModal: React.Dispatch<React.SetStateAction<void>>;
  serverOrder: IServerOrder;
};

const ModalOrderDetails = ({ showModal, onCloseModal, serverOrder }: ModalOrderDetailsProps) => {
  const { cart, loyaltyTransaction } = serverOrder;
  const { cartTotalCents } = useCartWithServerOrderSurcharges(cart);

  // This modal is also reachable via a direct rbiOrderId deep link/push notification (see
  // app/(modals)/order-confirmation/[rbiOrderId].tsx + useOrderStatus, fetched by id alone with
  // no store scoping), so the currently selected store can differ from the order's own store —
  // One Menu relocalization is only safe when they match. Compares against loadedStoreNumber
  // (set only once indexedMenu's fetch for that store completes), not useStoreContext()'s
  // store.number, since indexedMenu can still hold the previous store's menu for a render or
  // two after store.number updates, until UnifiedMenuProvider's effects catch up. Also excluded
  // while isLoading, for the same reason.
  const { loadedStoreNumber, isLoading: isMenuLoading } = useUnifiedMenuContext();
  const isCurrentStoreOrderStore =
    !isMenuLoading &&
    !!loadedStoreNumber &&
    !!cart.storeDetails?.storeNumber &&
    loadedStoreNumber === cart.storeDetails.storeNumber;

  return (
    <BottomSheet onOpenEventMessage="View Order Details" isOpen={showModal} onClose={onCloseModal}>
      <Container>
        <CenteredRowContainer>
          {cart.cartEntries.map(entry => (
            <Fragment key={entry.lineId}>
              <OrderItem cartEntry={entry} allowOneMenuRelocalization={isCurrentStoreOrderStore} />
            </Fragment>
          ))}
          <OrderPricesDetails serverOrder={serverOrder} />
          {loyaltyTransaction && <OrderRewardDetails loyaltyTransaction={loyaltyTransaction} />}
          <TotalsRow>
            <TotalLabelTitle>{t('total')}</TotalLabelTitle>
            <Total fontSize="md">
              <Currency isBold amount={cartTotalCents} />
            </Total>
          </TotalsRow>
        </CenteredRowContainer>
      </Container>
    </BottomSheet>
  );
};

export default ModalOrderDetails;
